"use client";

import { Keyboard } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/src/components/ui/dialog";
import SettingsSheetButton from "./SettingsSheetButton";

const shortcuts = [
  {
    category: "General",
    items: [
      { keys: ["⌘", "S"], description: "Save document", windowsKeys: ["Ctrl", "S"] },
      { keys: ["⌘", "K"], description: "Search the book", windowsKeys: ["Ctrl", "K"] },
      { keys: ["Ctrl", "Shift", "L"], description: "Ask Quarkle about selection", windowsKeys: ["Ctrl", "Shift", "L"] },
      { keys: ["Ctrl", "Shift", "?"], description: "Show keyboard shortcuts" },
    ],
  },
  {
    category: "Navigation",
    items: [
      { keys: ["Ctrl", "↑"], description: "Jump to previous occurrence of word" },
      { keys: ["Ctrl", "↓"], description: "Jump to next occurrence of word" },
    ],
  },
  {
    category: "Text Formatting",
    items: [
      { keys: ["⌘", "B"], description: "Bold", windowsKeys: ["Ctrl", "B"] },
      { keys: ["⌘", "I"], description: "Italic", windowsKeys: ["Ctrl", "I"] },
      { keys: ["⌘", "U"], description: "Underline", windowsKeys: ["Ctrl", "U"] },
    ],
  },
];

function KeyboardKey({ children }) {
  return (
    <kbd
      className="qk-mono inline-flex h-6 min-w-[24px] items-center justify-center rounded-md px-1.5"
      style={{
        border: "1px solid var(--qk-ink)",
        background: "var(--qk-paper)",
        color: "var(--qk-ink)",
        fontSize: 11,
        letterSpacing: "0.05em",
        fontWeight: 500,
        boxShadow: "1px 1px 0 var(--qk-ink)",
      }}
    >
      {children}
    </kbd>
  );
}

function ShortcutRow({ shortcut }) {
  const isMac = typeof navigator !== "undefined" && navigator.platform.toUpperCase().indexOf("MAC") >= 0;
  const keys = isMac ? shortcut.keys : shortcut.windowsKeys || shortcut.keys;

  return (
    <div className="flex items-center justify-between py-2">
      <span
        className="qk-serif-body"
        style={{ fontSize: 14, fontStyle: "italic", color: "var(--qk-ink-2)" }}
      >
        {shortcut.description}
      </span>
      <div className="flex items-center gap-1">
        {keys.map((key, index) => (
          <span key={index} className="flex items-center">
            <KeyboardKey>{key}</KeyboardKey>
            {index < keys.length - 1 && (
              <span className="qk-mono mx-0.5" style={{ fontSize: 11, color: "var(--qk-muted)" }}>+</span>
            )}
          </span>
        ))}
      </div>
    </div>
  );
}

// Standalone dialog content (for use with external trigger)
export function KeyboardShortcutsContent() {
  return (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Keyboard className="h-4 w-4" style={{ color: "var(--qk-pink-deep)" }} />
          <span>Keyboard Shortcuts</span>
        </DialogTitle>
      </DialogHeader>
      <div className="mt-3 space-y-5">
        {shortcuts.map((section) => (
          <div key={section.category}>
            <h3 className="qk-caps mb-2 px-3" style={{ color: "var(--qk-muted)" }}>
              {section.category.toLowerCase()}
            </h3>
            <div
              className="rounded-xl px-3"
              style={{
                background: "var(--qk-paper-3)",
                border: "1px solid var(--qk-rule)",
              }}
            >
              {section.items.map((shortcut, index) => (
                <div
                  key={index}
                  style={index === 0 ? {} : { borderTop: "1px dashed var(--qk-rule)" }}
                >
                  <ShortcutRow shortcut={shortcut} />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <p className="qk-hand mt-4 text-center" style={{ fontSize: 14, color: "var(--qk-muted)" }}>
        press <KeyboardKey>Ctrl</KeyboardKey>
        <span className="mx-0.5">+</span>
        <KeyboardKey>Shift</KeyboardKey>
        <span className="mx-0.5">+</span>
        <KeyboardKey>?</KeyboardKey> any time
      </p>
    </>
  );
}

// Settings button version (for use in settings sheet)
export default function KeyboardShortcutsDialog() {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <SettingsSheetButton icon={<Keyboard className="h-5 w-5" />} label="Keyboard Shortcuts" />
      </DialogTrigger>
      <DialogContent className="max-h-[80vh] overflow-y-auto sm:max-w-[500px]">
        <KeyboardShortcutsContent />
      </DialogContent>
    </Dialog>
  );
}

// Global keyboard shortcuts dialog - accepts open/setOpen from parent
export function GlobalKeyboardShortcutsDialog({ open, setOpen }) {
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-h-[80vh] overflow-y-auto sm:max-w-[500px]">
        <KeyboardShortcutsContent />
      </DialogContent>
    </Dialog>
  );
}
